import React, { useCallback, useEffect, useState } from 'react';
import { Button, Empty, Skeleton, Space, Tag, Typography, message } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Interview } from '../models/interview.model';
import { InterviewService } from '../services/interview.service';
import ProposeInterviewModal from './ProposeInterviewModal';

interface Props {
  jobApplicationId: string;
  /** When true (closed application), skip API calls and hide scheduling actions. */
  disabled?: boolean;
}

const statusColor: Record<string, string> = {
  PROPOSED: 'blue',
  RESCHEDULED: 'geekblue',
  CONFIRMED: 'green',
  CANCELLED: 'default',
  COMPLETED: 'purple',
  NO_SHOW: 'orange',
};

const InterviewPanel: React.FC<Props> = ({ jobApplicationId, disabled = false }) => {
  const [items, setItems] = useState<Interview[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [rescheduleTarget, setRescheduleTarget] = useState<Interview | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await InterviewService.listForApplication(jobApplicationId);
      setItems(Array.isArray(data) ? data : []);
    } catch {
      message.error('Could not load interviews');
    } finally {
      setLoading(false);
    }
  }, [jobApplicationId]);

  useEffect(() => {
    if (disabled) {
      setItems([]);
      setLoading(false);
      return;
    }
    load();
  }, [disabled, load]);

  const cancel = async (interview: Interview) => {
    if (disabled) return;
    try {
      await InterviewService.cancel(interview.id);
      message.success('Interview cancelled');
      load();
    } catch {
      message.error('Could not cancel interview');
    }
  };

  const mark = async (interview: Interview, status: 'COMPLETED' | 'NO_SHOW') => {
    if (disabled) return;
    try {
      await InterviewService.markResult(interview.id, status);
      message.success(`Marked as ${status}`);
      load();
    } catch {
      message.error('Could not mark result');
    }
  };

  return (
    <section aria-labelledby="interviews-heading">
      <div className="interview-panel__header">
        <div>
          <Typography.Title id="interviews-heading" level={5} className="!mb-0">
            Interviews
          </Typography.Title>
          <Typography.Text type="secondary" className="text-xs">
            {disabled
              ? 'Unavailable for closed applications'
              : 'Schedule and track conversations'}
          </Typography.Text>
        </div>
        <Button
          type="primary"
          size="small"
          icon={<CalendarOutlined />}
          disabled={disabled}
          onClick={() => {
            setRescheduleTarget(null);
            setModalOpen(true);
          }}
        >
          Propose interview
        </Button>
      </div>

      {disabled ? (
        <Empty
          className="interview-panel__empty"
          description="Reopen the application to schedule interviews"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : null}

      {!disabled && loading ? <Skeleton active paragraph={{ rows: 2 }} title={false} /> : null}

      {!disabled && !loading && items.length === 0 ? (
        <Empty
          className="interview-panel__empty"
          description="No interviews scheduled"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : null}

      {!disabled ? (
        <Space direction="vertical" className="w-full" size="small">
          {items.map((interview) => (
            <article key={interview.id} className="interview-card">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <Typography.Text strong>{interview.title}</Typography.Text>
                <Tag color={statusColor[interview.status] || 'default'}>{interview.status}</Tag>
              </div>
              <Typography.Paragraph type="secondary" className="!mb-2">
                {interview.meetingType} · {interview.durationMinutes} min
                {interview.locationOrLink ? ` · ${interview.locationOrLink}` : ''}
              </Typography.Paragraph>
              {interview.confirmedStartsAt && (
                <Typography.Paragraph className="!mb-2">
                  Confirmed:{' '}
                  {dayjs(interview.confirmedStartsAt).format('YYYY-MM-DD HH:mm')} UTC
                </Typography.Paragraph>
              )}
              {!interview.confirmedStartsAt && interview.slots?.length > 0 && (
                <Typography.Paragraph className="!mb-2">
                  Proposed slots:{' '}
                  {interview.slots
                    .map((s) => dayjs(s.startsAt).format('YYYY-MM-DD HH:mm'))
                    .join(' · ')}{' '}
                  UTC
                </Typography.Paragraph>
              )}
              {interview.historyNote && (
                <Typography.Paragraph type="secondary" className="!mb-2">
                  {interview.historyNote}
                </Typography.Paragraph>
              )}
              <Space wrap>
                {(interview.status === 'PROPOSED' ||
                  interview.status === 'RESCHEDULED' ||
                  interview.status === 'CONFIRMED') && (
                  <Button
                    size="small"
                    onClick={() => {
                      setRescheduleTarget(interview);
                      setModalOpen(true);
                    }}
                  >
                    Reschedule
                  </Button>
                )}
                {interview.status !== 'CANCELLED' && interview.status !== 'COMPLETED' && (
                  <Button size="small" danger onClick={() => cancel(interview)}>
                    Cancel
                  </Button>
                )}
                {interview.status === 'CONFIRMED' && (
                  <>
                    <Button size="small" onClick={() => mark(interview, 'COMPLETED')}>
                      Mark completed
                    </Button>
                    <Button size="small" onClick={() => mark(interview, 'NO_SHOW')}>
                      Mark no-show
                    </Button>
                  </>
                )}
              </Space>
            </article>
          ))}
        </Space>
      ) : null}

      {!disabled ? (
        <ProposeInterviewModal
          open={modalOpen}
          jobApplicationId={jobApplicationId}
          interview={rescheduleTarget}
          onClose={() => {
            setModalOpen(false);
            setRescheduleTarget(null);
          }}
          onSaved={load}
        />
      ) : null}
    </section>
  );
};

export default InterviewPanel;
